import { v4 as uuid } from 'uuid';
import { isBooleanTrue, isListNotEmpty, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import ProductDimentions from "./ProductDimensions";
import ProductVariantSupplierMapping from "./ProductVariantSupplierMapping";

class Variant {
    id = uuid();
    productId = null;
    name = null;
    description = null;
    slug = convertToSlug(this.name);

    enabled = false;

    attributes = []; // array of product attributes

    dimensions = new ProductDimentions().toDto();
    weight = 0.0;

    imageRefId = null;

    metaData = []; // array of product meta data

    regularPrice = 0.0; // per unit price
    salePrice = null; // optional and will override the regular price
    compareAtPrice = null; // optional and will show a strike through as if the product is on discount
    isSale = false; // is the product on sale, use salePrice instead if true

    sku = null;

    manageStock = false;
    lowStockAmount = 0;
    stockStatus = "IN_STOCK"; // Enum StockStatuses
    stockQuantity = 0;

    displayOrder = 0;

    isVirtual = false; // is the product a service with no physical nature

    brand = null;
    gtin = null;
    msrp = null;

    duration = 0; // added 12/5/2023 for services such as telehealth visits in minutes

    count = 1;
    countUnits = null;
    packaging = null;

    // for medications
    instructions = null;
    strength = null;
    form = null; // form of the medication (e.g., tablet, liquid)
    route = null; // route of administration (e.g., oral, intravenous)

    requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
    requiresClinicianApproval = false; // requires that the doctor prescribes/approves the variant
    requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt to receive this variant if not already receiving this variant
    requiresSurveys = false; // requires patient to fill out forms/surveys

    // require documents to be provided when ordering variant
    requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes

    titrationLevel = 1; // the index of the dosage
    titrationPeriods = 1; // number of periods that patient has to stay at this dosage before moving up to a higher dosage
    titrationSchedule = 'NONE'; // type of titration schedule NONE, STANDARD, ALTERNATIVE, SPLIT, RAPID

    supplierMappings = [];  // array of ProductVariantSupplierMapping

    upcharge = 0;

    constructor(variant) {
        if (isObjectNotEmpty(variant)) {
            if (isStringNotEmpty(variant.id)) { this.id = variant.id; }
            if (isStringNotEmpty(variant.productId)) { this.productId = variant.productId; }
            if (isStringNotEmpty(variant.name)) { this.name = variant.name; }
            if (isStringNotEmpty(variant.slug)) { this.slug = variant.slug; }
            else { this.slug = convertToSlug(this.name); }
            if (isStringNotEmpty(variant.description)) { this.description = variant.description; }

            if (isBooleanTrue(variant.enabled)) { this.enabled = true; }

            if (isListNotEmpty(variant.attributes)) { this.attributes = variant.attributes; }

            if (isNumberNotEmpty(variant.width) && isNumberNotEmpty(variant.height) && isNumberNotEmpty(variant.length)) { this.dimensions = new ProductDimentions(variant.width, variant.height, variant.length).toDto(); }
            if (isNumberGreaterThanOrEqualToZero(variant.weight)) { this.weight = variant.weight; }

            if (isStringNotEmpty(variant.imageRefId)) { this.imageRefId = variant.imageRefId; }

            if (isListNotEmpty(variant.metaData)) { this.metaData = variant.metaData; }

            if (isNumberGreaterThanOrEqualToZero(variant.regularPrice)) { this.regularPrice = variant.regularPrice; }
            if (isNumberGreaterThanOrEqualToZero(variant.salePrice)) { this.salePrice = variant.salePrice; }
            if (isNumberGreaterThanOrEqualToZero(variant.compareAtPrice)) { this.compareAtPrice = variant.compareAtPrice; }
            if (isBooleanTrue(variant.isSale)) { this.isSale = true; }

            if (isStringNotEmpty(variant.sku)) { this.sku = variant.sku; }

            if (isBooleanTrue(variant.manageStock)) { this.manageStock = true; }
            if (isStringNotEmpty(variant.stockStatus)) { this.stockStatus = variant.stockStatus; }
            if (isNumberGreaterThanOrEqualToZero(variant.stockQuantity)) { this.stockQuantity = variant.stockQuantity; }
            if (isNumberGreaterThanOrEqualToZero(variant.lowStockAmount)) { this.lowStockAmount = variant.lowStockAmount; }

            if (isNumberGreaterThanOrEqualToZero(variant.displayOrder)) { this.displayOrder = variant.displayOrder; }

            if (isBooleanTrue(variant.isVirtual)) { this.isVirtual = true; }

            if (isNumberGreaterThanOrEqualToZero(variant.msrp)) { this.msrp = variant.msrp; }
            if (isStringNotEmpty(variant.brand)) { this.brand = variant.brand; }
            if (isStringNotEmpty(variant.gtin)) { this.gtin = variant.gtin; }

            if (isNumberGreaterThanOrEqualToZero(variant.duration)) { this.duration = variant.duration; }

            if (isNumberNotEmpty(variant.count)) { this.count = variant.count; }
            if (isStringNotEmpty(variant.countUnits)) { this.countUnits = variant.countUnits; }
            if (isStringNotEmpty(variant.packaging)) { this.packaging = variant.packaging; }

            if (isNumberNotEmpty(variant.count)) { this.count = variant.count; }
            if (isStringNotEmpty(variant.countUnits)) { this.countUnits = variant.countUnits; }
            if (isStringNotEmpty(variant.packaging)) { this.packaging = variant.packaging; }

            if (isStringNotEmpty(variant.instructions)) { this.instructions = variant.instructions; }
            if (isStringNotEmpty(variant.strength)) { this.strength = variant.strength; }
            if (isStringNotEmpty(variant.form)) { this.form = variant.form; }
            if (isStringNotEmpty(variant.route)) { this.route = variant.route; }

            if (isBooleanTrue(variant.requiresConfirmation)) { this.requiresConfirmation = true; }
            if (isBooleanTrue(variant.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
            if (isBooleanTrue(variant.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
            if (isBooleanTrue(variant.requiresSurveys)) { this.requiresSurveys = true; }

            if (isListNotEmpty(variant.requiredDocuments)) { this.requiredDocuments = variant.requiredDocuments; }

            if (isNumberGreaterThanOrEqualToZero(variant.titrationLevel)) { this.titrationLevel = variant.titrationLevel; }
            if (isNumberGreaterThanOrEqualToZero(variant.titrationPeriods)) { this.titrationPeriods = variant.titrationPeriods; }
            if (isStringNotEmpty(variant.titrationSchedule)) { this.titrationSchedule = variant.titrationSchedule; }

            if (isListNotEmpty(variant.supplierMappings)) { this.supplierMappings = variant.supplierMappings.map((supplierMapping) => new ProductVariantSupplierMapping(supplierMapping).toDto()); }

            if (isNumberGreaterThanOrEqualToZero(variant.upcharge)) { this.upcharge = variant.upcharge; }
        }
    }

    toDto = () => {
        return {
            id: this.id,
            productId: this.productId,
            name: this.name,
            description: this.description,
            slug: this.slug,

            enabled: this.enabled,

            attributes: this.attributes,

            dimensions: this.dimensions,
            weight: this.weight,

            imageRefId: this.imageRefId,

            metaData: this.metaData,

            regularPrice: this.regularPrice,
            salePrice: this.salePrice,
            compareAtPrice: this.compareAtPrice,
            isSale: this.isSale,

            sku: this.sku,

            manageStock: this.manageStock,
            stockStatus: this.stockStatus,
            stockQuantity: this.stockQuantity,
            lowStockAmount: this.lowStockAmount,

            displayOrder: this.displayOrder,

            isVirtual: this.isVirtual,

            brand: this.brand,
            gtin: this.gtin,
            msrp: this.msrp,

            duration: this.duration,

            count: this.count,
            countUnits: this.countUnits,
            packaging: this.packaging,

            instructions: this.instructions,
            strength: this.strength,
            form: this.form,
            route: this.route,

            requiresClinicianEncounter: this.requiresClinicianEncounter,

            requiresConfirmation: this.requiresConfirmation,
            requiresClinicianApproval: this.requiresClinicianApproval,
            requiresClinicianEncounter: this.requiresClinicianEncounter,
            requiresSurveys: this.requiresSurveys,

            requiredDocuments: this.requiredDocuments,

            titrationLevel: this.titrationLevel,
            titrationPeriods: this.titrationPeriods,
            titrationSchedule: this.titrationSchedule,

            supplierMappings: this.supplierMappings,

            upcharge: this.upcharge,
        };
    }

    getPrice = () => {
        if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
            return this.salePrice;
        } else {
            return this.regularPrice;
        }
    }

    getComparePrice = () => {
        let price = 0;
        let savings = 0;
        let savingsPercentage = 0;
        if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
            price = this.regularPrice;
            savings = this.regularPrice - this.salePrice;
            savingsPercentage = (savings / price) * 100;
        } else if (isNumberGreaterThanZero(this.msrp)) {
            price = this.msrp;
            savings = this.msrp - this.regularPrice;
            savingsPercentage = (savings / price) * 100;
        } else if (isNumberGreaterThanZero(this.compareAtPrice)) {
            price = this.compareAtPrice;
            savings = this.compareAtPrice - this.regularPrice;
            savingsPercentage = (savings / price) * 100;
        } else {
            price = this.regularPrice;
            savings = 0;
            savingsPercentage = 0;
        }

        return { price, savings, savingsPercentage };
    }
};

export default Variant;